import * as React from "react";
import {
  AlertCircle,
  CalendarPlus,
  CheckCircle2,
  FileClock,
  Layers,
  RefreshCw,
  Wallet,
} from "lucide-react";
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  StatCard,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@eduviaos/ui-kit";
import { GeneratePayRunSheet } from "@/components/payroll/GeneratePayRunSheet";
import { PayRunDetailSheet } from "@/components/payroll/PayRunDetailSheet";
import { useCanManagePayroll } from "@/hooks/usePayrollAuthoring";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { useTerms } from "@/hooks/useTerms";
import {
  formatPaise,
  formatPaiseCompact,
  payPeriodLabel,
  usePayRuns,
  usePayrollSummary,
  type PayRun,
  type PayRunStatus,
} from "@/lib/payroll/api";

/**
 * Phase SC-2c — IA owner-payroll workspace.
 *
 * A study centre is owner-operated, so "payroll" is the owner running the
 * team's monthly pay cycle. This surface:
 *   - summarises the payroll picture (runs, drafts, latest net payout);
 *   - lists pay runs (`<DataTable>`, Iron Rule #16), newest first;
 *   - lets the owner draft a new run (seeded from the active staff-cost
 *     ledger), edit payslips, and finalize — which makes that month's gross
 *     the authoritative staff-cost line in the accounting P&L.
 *
 * Reads gate on `view_payroll`; write affordances on `manage_payroll`
 * (admin-ceiling by default). The route is wrapped in
 * `<RouteModuleGate module="payroll">` in `App.tsx`, and the BE re-checks
 * module + permission on every call.
 */

const STATUS_FILTER_ANY = "__any__";

function PayrollHeader({ right }: { right?: React.ReactNode }) {
  const { ct } = useTerms();
  return (
    <PageHeader>
      <PageHeaderTop>
        <PageHeaderTitle>
          <PageHeaderHeading>Payroll</PageHeaderHeading>
          <PageHeaderDescription>
            {ct(
              "Run your team's monthly pay cycle. Finalized runs become that month's staff cost in your profit & loss.",
            )}
          </PageHeaderDescription>
        </PageHeaderTitle>
        {right ? <PageHeaderActions>{right}</PageHeaderActions> : null}
      </PageHeaderTop>
    </PageHeader>
  );
}

/**
 * `embedded` renders the runs workspace as the Team pay "Pay runs" tab
 * (Phase SC-22d): the page header moves into an inline action row.
 */
export function PayrollPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { canViewPayroll, canManagePayroll, missingView } = useCanManagePayroll();
  const { ct } = useTerms();

  const [statusFilter, setStatusFilter] = React.useState<string>(STATUS_FILTER_ANY);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "payroll-runs",
    defaultPageSize: 10,
  });
  const [generateOpen, setGenerateOpen] = React.useState(false);
  const [selectedRunId, setSelectedRunId] = React.useState<string | null>(null);
  const [detailOpen, setDetailOpen] = React.useState(false);

  const summary = usePayrollSummary();
  const runsQuery = usePayRuns({
    status: statusFilter === STATUS_FILTER_ANY ? undefined : (statusFilter as PayRunStatus),
    limit: pageSize,
    offset: page * pageSize,
  });
  const { data, isLoading, isFetching, isError, refetch } = runsQuery;

  const total = data?.total ?? 0;
  const items = React.useMemo(() => data?.items ?? [], [data]);

  const openRun = (run: PayRun) => {
    setSelectedRunId(run.id);
    setDetailOpen(true);
  };

  if (missingView) {
    return (
      <div className="space-y-6">
        {embedded ? null : <PayrollHeader />}
        <Card className="border-amber-300/60">
          <CardHeader>
            <CardTitle className="text-base">Restricted area</CardTitle>
            <CardDescription>
              Payroll is visible to administrators only. Ask an admin to grant the{" "}
              <span className="font-medium">View payroll</span> permission if you need access.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {embedded ? (
        canManagePayroll ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-muted-foreground max-w-prose text-sm">
              Finalized runs become that month&apos;s team cost in your profit &amp; loss.
            </p>
            <Button size="sm" onClick={() => setGenerateOpen(true)} data-testid="generate-run">
              <CalendarPlus aria-hidden="true" className="size-4" />
              Generate pay run
            </Button>
          </div>
        ) : null
      ) : (
        <PayrollHeader
          right={
            canManagePayroll ? (
              <Button size="sm" onClick={() => setGenerateOpen(true)} data-testid="generate-run">
                <CalendarPlus aria-hidden="true" className="size-4" />
                Generate pay run
              </Button>
            ) : undefined
          }
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Pay runs"
          value={summary.isLoading ? "—" : String(summary.data?.total_runs ?? 0)}
          caption="All months on record"
          icon={<Layers />}
          tone="blue"
        />
        <StatCard
          label="Drafts"
          value={summary.isLoading ? "—" : String(summary.data?.draft_runs ?? 0)}
          caption="Awaiting finalization"
          icon={<FileClock />}
          tone="amber"
        />
        <StatCard
          label="Finalized"
          value={summary.isLoading ? "—" : String(summary.data?.finalized_runs ?? 0)}
          caption="Locked into the P&L"
          icon={<CheckCircle2 />}
          tone="green"
        />
        <StatCard
          label="Latest net payout"
          value={
            summary.isLoading
              ? "—"
              : formatPaiseCompact(summary.data?.latest_finalized_net_paise ?? 0)
          }
          caption="Most recent finalized run"
          icon={<Wallet />}
          tone="violet"
        />
      </div>

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">Couldn&apos;t load pay runs.</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <DataTable>
        <DataTableToolbar>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            aria-label="Status filter"
            className="border-input bg-background h-9 rounded-md border px-2 text-sm"
          >
            <option value={STATUS_FILTER_ANY}>Any status</option>
            <option value="draft">Draft</option>
            <option value="finalized">Finalized</option>
          </select>
          <DataTableToolbarSpacer />
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw aria-hidden="true" className={cn("size-4", isFetching && "animate-spin")} />
            Refresh
          </Button>
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[28%]">Period</TableHead>
                <TableHead className="w-[14%]">Status</TableHead>
                <TableHead className="w-[14%] text-right">Payslips</TableHead>
                <TableHead className="w-[22%] text-right">Gross</TableHead>
                <TableHead className="w-[22%] text-right">Net payout</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={5} />
            ) : (
              <TableBody>
                {items.map((run) => (
                  <TableRow
                    key={run.id}
                    data-testid={`pay-run-row-${run.id}`}
                    className="hover:bg-muted/40 cursor-pointer"
                    onClick={() => openRun(run)}
                  >
                    <TableCell className="font-medium">
                      <button
                        type="button"
                        className="hover:text-primary text-left focus:outline-none"
                        onClick={(e) => {
                          e.stopPropagation();
                          openRun(run);
                        }}
                      >
                        {payPeriodLabel(run.year, run.month)}
                      </button>
                    </TableCell>
                    <TableCell>
                      <StatusPill
                        status={run.status === "finalized" ? "active" : "invited"}
                        label={run.status === "finalized" ? "Finalized" : "Draft"}
                      />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{run.payslip_count}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatPaise(run.gross_total_paise)}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatPaise(run.net_total_paise)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<Wallet aria-hidden="true" />}
            title="No pay runs yet"
            description={
              canManagePayroll
                ? ct(
                    "Generate your first monthly pay run — we'll start it from your active staff costs.",
                  )
                : "No pay runs have been generated yet."
            }
            action={
              canManagePayroll ? (
                <Button size="sm" onClick={() => setGenerateOpen(true)}>
                  <CalendarPlus aria-hidden="true" className="size-4" />
                  Generate pay run
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && items.length > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              {total.toLocaleString()} pay run{total === 1 ? "" : "s"}
            </span>
            <DataTablePagination
              className="ml-auto"
              page={page + 1}
              pageSize={pageSize}
              totalRows={total}
              isFetching={isFetching}
              onPageChange={(next) => setPage(Math.max(0, next - 1))}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(0);
              }}
              testIdPrefix="pay-runs-pagination"
            />
          </DataTableFooter>
        ) : null}
      </DataTable>

      {canManagePayroll ? (
        <GeneratePayRunSheet
          open={generateOpen}
          onOpenChange={setGenerateOpen}
          onGenerated={(detail) => {
            setSelectedRunId(detail.run.id);
            setDetailOpen(true);
          }}
        />
      ) : null}

      {canViewPayroll ? (
        <PayRunDetailSheet
          open={detailOpen}
          onOpenChange={setDetailOpen}
          runId={selectedRunId}
          canManage={canManagePayroll}
          onDeleted={() => setSelectedRunId(null)}
        />
      ) : null}
    </div>
  );
}
